import { useState, useEffect } from 'react';
import { Card, CardBody, Button, Input } from '@components/ui';
import { BuildingOfficeIcon, UserCircleIcon, MapPinIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { InvoiceWizardData } from '../../types/invoiceWizard.types';
import { CustomerSearchModal } from './CustomerSearchModal';
import { AddressModal } from './AddressModal';
import { BranchOfficeFormModal } from '@features/companies/components/BranchOfficeFormModal';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { useBranchOffices } from '@features/companies/hooks/useBranchOffices';
import { useCountries } from '@features/customers/hooks/useGeolocation';
import { branchOfficeService } from '@features/branchOffice/services/branchOfficeService';
import { companyService } from '@features/company/services/companyService';

interface Step1CustomerSelectionProps {
  wizardData: InvoiceWizardData;
  onUpdate: (updates: Partial<InvoiceWizardData>) => void;
  onNext: () => void;
}

const RECEIVER_TYPES = [
  { value: '01', label: 'Contribuyente' },
  { value: '02', label: 'Consumidor Final' },
  { value: '03', label: 'Gobierno' },
  { value: '04', label: 'Extranjero' },
];

// Tipo de Documento según tipo de receptor DGI:
// - Contribuyente (01) / Gobierno (03): Solo RUC (gRucRec obligatorio)
// - Consumidor Final (02): RUC o Cédula (ambos van al XML en dRuc si el cliente lo solicita, DGI B4022)
// - Extranjero (04): No se muestra selector (usa gIdExt)
const DOCUMENT_TYPES_BY_RECEIVER: Record<string, Array<{ value: string; label: string }>> = {
  '01': [ // Contribuyente: solo RUC
    { value: '01', label: 'RUC - Registro Único del Contribuyente' },
  ],
  '02': [ // Consumidor Final: RUC opcional o Cédula
    { value: '01', label: 'RUC - Registro Único del Contribuyente' },
    { value: '02', label: 'Cédula - Cédula de Identidad Personal' },
  ],
  '03': [ // Gobierno: solo RUC
    { value: '01', label: 'RUC - Registro Único del Contribuyente' },
  ],
};

// Tipo de RUC (Natural o Jurídico)
const RUC_PERSON_TYPES = [
  { value: '1', label: 'Natural' },
  { value: '2', label: 'Jurídico' },
];

export function Step1CustomerSelection({ wizardData, onUpdate, onNext }: Step1CustomerSelectionProps) {
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isBranchOfficeModalOpen, setIsBranchOfficeModalOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null); // Store selected customer for address modal

  // Obtener datos de la compañía seleccionada y sucursales
  const { selectedCompany, isLoading: companyLoading, refetchSelectedCompany } = useCompanies({ navigateOnSelect: false });
  const { data: branchOfficesData, isLoading: loadingBranches } = useBranchOffices();
  const { data: countries } = useCountries();

  // Refetch selected company data when component mounts to ensure fresh data
  useEffect(() => {
    console.log('🔄 Step1 montado - Refrescando datos de compañía seleccionada...');
    refetchSelectedCompany();
  }, [refetchSelectedCompany]); // Solo al montar

  // Extraer el array de sucursales del objeto de respuesta
  const branchOffices = (branchOfficesData as any)?.branchOffices || [];

  console.log('🏢 Step1 - Company:', selectedCompany);
  console.log('🏢 Step1 - Branch Offices Data:', branchOfficesData);
  console.log('🏢 Step1 - Branch Offices Array:', branchOffices);
  if (branchOffices.length > 0) {
    console.log('🏢 Step1 - Sample Branch Office:', branchOffices[0]);
  }

  // Update wizard data when company is selected
  useEffect(() => {
    if (selectedCompany && !companyLoading) {
      console.log('📝 Updating wizard data with company info:', selectedCompany);
      console.log('📝 Current issuerName in wizard:', wizardData.issuerName);

      // Solo actualizar si los datos del emisor están vacíos o son diferentes
      // Esto evita sobrescribir datos cuando ya están cargados
      // El emisor de la factura lleva la razón social (fullName), con nombre comercial como fallback
      const companyIssuerName = selectedCompany.fullName || selectedCompany.name || '';
      const shouldUpdate = !wizardData.issuerName ||
                          wizardData.issuerName !== companyIssuerName ||
                          !wizardData.issuerCode ||
                          wizardData.issuerCode !== selectedCompany.numberId ||
                          !wizardData.issuerCompanyCode ||
                          wizardData.issuerCompanyCode !== selectedCompany.code;

      if (shouldUpdate) {
        console.log('✅ Actualizando datos del emisor en el wizard');

        // Convertir TypeTaxpayer a string para el mapper
        const typeTaxpayer = selectedCompany.typeTaxpayer?.toString() || '2';

        onUpdate({
          issuerName: companyIssuerName,
          issuerCode: selectedCompany.numberId || '',
          issuerCompanyCode: selectedCompany.code || '',
          issuerDv: selectedCompany.dv || '',
          issuerTypeTaxpayer: typeTaxpayer, // 1=Natural, 2=Jurídico
          issuerEmail: selectedCompany.email || '',
          issuerPhone: selectedCompany.phone || '',
          issuerImage: selectedCompany.profileImage || '',
        });
      } else {
        console.log('ℹ️ Datos del emisor ya están actualizados, saltando actualización');
      }
    } else if (!selectedCompany && !companyLoading) {
      console.warn('⚠️ No hay compañía seleccionada después de cargar');
    }
  }, [selectedCompany, companyLoading, wizardData.issuerName, wizardData.issuerCode]);

  // Update wizard data when branch office is selected - FETCH FROM ALUDRA API
  useEffect(() => {
    const fetchBranchDetails = async () => {
      console.log('🔍 useEffect ejecutado - branchOfficeId:', wizardData.branchOfficeId, 'companyId:', selectedCompany?.id);

      if (wizardData.branchOfficeId && selectedCompany?.id) {
        try {
          console.log('🔄 Obteniendo detalles completos de la sucursal desde Aludra...');
          console.log('🔄 Parámetros: CompanyId =', selectedCompany.id, ', BranchOfficeId =', wizardData.branchOfficeId);

          // Llamar al API de Aludra para obtener detalles completos
          const branchDetails = await branchOfficeService.getBranchOfficeDetailsFromAludra(
            selectedCompany.id,
            wizardData.branchOfficeId
          );

          console.log('📝 Actualizando wizard con detalles de Aludra:', branchDetails);

          // Capturar CountryId del BranchOffice
          const countryId = branchDetails.CountryId || branchDetails.Address_CountryId;
          console.log('🌍 CountryId del BranchOffice:', countryId);

          // Valores por defecto (Panamá)
          let countryCode = 'PA';
          let countryName = 'PANAMÁ';

          // Si hay CountryId, buscar la información del país
          if (countryId) {
            try {
              console.log('🔄 Obteniendo información del país desde Aludra...');
              const countryData = await companyService.getCountryById(countryId);

              if (countryData?.Data && countryData.Data.length > 0) {
                const country = countryData.Data[0];
                countryCode = country.Code || 'PA';
                countryName = country.Name || 'PANAMÁ';
                console.log('✅ País obtenido:', { countryCode, countryName });
              }
            } catch (error) {
              console.warn('⚠️ Error al obtener información del país, usando Panamá por defecto:', error);
            }
          } else {
            console.log('ℹ️ CountryId es NULL, usando Panamá por defecto');
          }

          // Mapear los campos de Aludra al formato del wizard
          const updateData = {
            branchOfficeName: branchDetails.Description || branchDetails.Name || '',
            branchOfficeAddress: branchDetails.Address_AddressDetail || branchDetails.Address || '',
            branchOfficeAddressCode: branchDetails.Code || '',
            branchOfficeLatitude: branchDetails.Address_Latitude || '',
            branchOfficeLongitude: branchDetails.Address_Longitude || '',
            branchOfficeProvince: branchDetails.Address_ProvinceName || branchDetails.Address_Province || branchDetails.Province || '',
            branchOfficeDistrict: branchDetails.Address_DistrictName || branchDetails.Address_District || branchDetails.District || '',
            branchOfficeTownship: branchDetails.Address_TownshipName || branchDetails.Address_Township || branchDetails.Township || '',
            branchOfficeFiscalCode: branchDetails.Address_FiscalCode || branchDetails.AddressFiscalCode || branchDetails.FiscalCode || '',
            branchOfficeCountryId: countryId || '',
            branchOfficeCountryCode: countryCode,
            branchOfficeCountryName: countryName,
          };

          console.log('🔄 DATOS QUE SE VAN A ACTUALIZAR EN EL WIZARD:', JSON.stringify(updateData, null, 2));

          onUpdate(updateData);

          console.log('✅ Datos de sucursal actualizados correctamente');
        } catch (error) {
          console.error('❌ Error al obtener detalles de la sucursal:', error);

          // Fallback: usar datos locales si el API falla
          const selectedBranch = branchOffices.find((b: any) => b.id === wizardData.branchOfficeId);
          if (selectedBranch) {
            console.warn('⚠️ Usando datos locales de la sucursal (fallback)');
            onUpdate({
              branchOfficeName: selectedBranch.name || '',
              branchOfficeAddress: selectedBranch.address || '',
              branchOfficeAddressCode: selectedBranch.addressCode || selectedBranch.code || '',
              branchOfficeLatitude: selectedBranch.coordinates?.latitude?.toString() || '',
              branchOfficeLongitude: selectedBranch.coordinates?.longitude?.toString() || '',
              branchOfficeProvince: selectedBranch.province || '',
              branchOfficeDistrict: selectedBranch.district || '',
              branchOfficeTownship: selectedBranch.township || '',
              branchOfficeFiscalCode: selectedBranch.fiscalCode || '',
            });
          }
        }
      }
    };

    fetchBranchDetails();
  }, [wizardData.branchOfficeId, selectedCompany, branchOffices]);

  const handleSelectCustomer = (customer: any) => {
    // Store the selected customer for address modal
    setSelectedCustomer(customer);

    // Debug log to see what we're receiving
    console.log('📋 Customer selected:', {
      taxIdType: customer.taxIdType,
      taxId: customer.taxId,
      taxIdSub: customer.taxIdSub,
      fullCustomer: customer
    });

    // Get the default billing address or the first address
    const defaultAddress = customer.address?.find((addr: any) => addr.is_default && addr.type === 'BILLING')
      || customer.address?.[0];

    // Map tax_id_type to receiverType (iTipoRec)
    const getReceiverType = (taxIdType: string) => {
      if (!taxIdType) return '01';

      const normalized = taxIdType.toUpperCase().trim();
      console.log('🔍 Mapping taxIdType to iTipoRec:', normalized);

      // Si ya viene como código, retornarlo directamente
      if (normalized === '01' || normalized === '02' || normalized === '03' || normalized === '04') {
        return normalized;
      }

      // Check if it contains the keyword (more flexible matching)
      if (normalized.includes('CONTRIBUYENTE')) return '01';
      if (normalized.includes('CONSUMIDOR')) return '02';
      if (normalized.includes('GOBIERNO')) return '03';
      if (normalized.includes('EXTRANJERO')) return '04';

      console.warn('⚠️ Unknown taxIdType:', taxIdType, '- defaulting to Contribuyente');
      return '01'; // Default to Contribuyente
    };

    // Map customer's type field to document type code
    const getRucType = (customer: any) => {
      // Si customer.type ya viene como código, usarlo directamente
      if (customer.type) {
        const typeCode = customer.type.toString().trim();
        if (typeCode === '01' || typeCode === '02' || typeCode === '03' || typeCode === '04') {
          console.log('🔍 Using customer.type code:', typeCode);
          return typeCode;
        }
      }

      // Si customer.documentType existe, mapearlo
      if (customer.documentType) {
        const docType = customer.documentType?.toUpperCase();
        console.log('🔍 Mapping documentType:', docType);
        if (docType.includes('RUC')) return '01';
        if (docType.includes('CEDULA') || docType.includes('CÉDULA')) return '02';
        if (docType.includes('PASAPORTE')) return '03';
        if (docType.includes('OTRO')) return '04';
      }

      console.warn('⚠️ No type or documentType found, defaulting to RUC');
      return '01'; // Default to RUC
    };

    // Resolver código ISO del país a partir del nombre
    const receiverType = getReceiverType(customer.taxIdType);
    const countryName = defaultAddress?.country || '';
    const matchedCountry = countries?.find(
      (c) => c.Name.toUpperCase() === countryName.toUpperCase()
    );
    const countryCode = matchedCountry?.Code || '';

    onUpdate({
      customerId: customer.id,
      customerName: customer.name,
      customerRuc: customer.taxId, // tax_id from API
      customerDv: customer.taxIdSub || '', // tax_id_sub from API
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      receiverType, // Map tax_id_type (iTipoRec)
      customerRucType: getRucType(customer), // Map document type
      customerRecepType: customer.recep_type || '1', // Tipo de RUC (1=Natural, 2=Jurídico)
      // Address fields
      customerAddress: defaultAddress?.line1 || '',
      customerCountry: countryName,
      customerForeignCountry: countryCode, // Código ISO del país (ej: GT, CR, US)
      customerProvince: defaultAddress?.state || '',
      customerDistrict: defaultAddress?.district || '',
      customerTownship: defaultAddress?.township || defaultAddress?.town || '',
      customerCity: defaultAddress?.city || '',
      customerAddressFiscalCode: defaultAddress?.code_address || '',
      // Auto-ajustar destino de operación según tipo de receptor
      // No forzar destino para extranjero; se define en Step 2 según el tipo de operación
    });
    setIsCustomerModalOpen(false);
  };

  const handleAddressUpdate = (addressData: any) => {
    onUpdate({
      customerAddress: addressData.description,
      customerCountry: addressData.country,
      customerForeignCountry: addressData.countryCode || '', // Código ISO del país para clientes extranjeros
      customerProvince: addressData.province,
      customerDistrict: addressData.district,
      customerTownship: addressData.township,
      customerCity: addressData.city || '',
      customerAddressFiscalCode: addressData.fiscalCode,
    });
    setIsAddressModalOpen(false);
  };

  // Validación en vivo: mostrar errores DGI condicionales mientras el usuario edita
  useEffect(() => {
    setErrors(prev => {
      const updated = { ...prev };

      // Limpiar errores que ya se corrigieron
      if (wizardData.customerName && updated.customerName) delete updated.customerName;
      if (wizardData.customerAddress && updated.customerAddress) delete updated.customerAddress;
      if (wizardData.customerProvince && updated.customerProvince) delete updated.customerProvince;
      if (wizardData.customerEmail && updated.customerEmail) delete updated.customerEmail;
      if (wizardData.customerRuc && updated.customerRuc) delete updated.customerRuc;
      if (wizardData.customerDv && updated.customerDv) delete updated.customerDv;
      if (wizardData.branchOfficeId && updated.branchOffice) delete updated.branchOffice;

      // Solo mostrar errores DGI condicionales si el usuario ya seleccionó un cliente
      if (wizardData.customerId) {
        // DGI B404: Dirección obligatoria solo para Contribuyente (01) y Gobierno (03), opcional para 02/04
        const needsAddress = wizardData.receiverType === '01' || wizardData.receiverType === '03';
        if (needsAddress && !wizardData.customerAddress) {
          updated.customerAddress = 'Dirección obligatoria para el receptor (DGI B404)';
        }
        if (!wizardData.customerForeignCountry?.trim()) {
          updated.customerCountry = 'País del receptor obligatorio (DGI B410)';
        }
        // DGI B405: Ubicación solo obligatoria para Contribuyente (01) y Gobierno (03)
        const isContrib = wizardData.receiverType === '01';
        const isGob = wizardData.receiverType === '03';
        if ((isContrib || isGob) && !wizardData.customerProvince) {
          updated.customerProvince = 'Ubicación obligatoria para Contribuyente/Gobierno (DGI B405)';
        }

        // Limpiar errores de país antes de re-evaluar
        delete updated.customerCountry;

        // NOTA: B410a y B410b no se validan aquí porque el destino se configura en Step 2.
        // Estas validaciones se ejecutan en el invoiceMapper antes de enviar a Aludra.
      }

      // DGI B08 (1515): Punto de facturación "000" → R
      if (wizardData.billingPointCode === '000') {
        updated.billingPoint = 'El punto de facturación no puede ser "000" (DGI B08 código 1515)';
      } else if (updated.billingPoint) {
        delete updated.billingPoint;
      }

      return updated;
    });
  }, [wizardData.receiverType, wizardData.customerName, wizardData.customerAddress, wizardData.customerProvince, wizardData.customerEmail, wizardData.customerRuc, wizardData.customerDv, wizardData.branchOfficeId, wizardData.customerId, wizardData.operationDestination, wizardData.customerForeignCountry, wizardData.billingPointCode]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Validación de la sucursal (obligatoria)
    if (!wizardData.branchOfficeId) {
      newErrors.branchOffice = 'Debe seleccionar una sucursal';
    }

    const isContribuyente = wizardData.receiverType === '01';
    const isGobierno = wizardData.receiverType === '03';

    // --- Validaciones del Receptor ---

    // DGI B403: Nombre obligatorio si tipo = 01, 03 o 04 (R), opcional para 02
    const isExtranjero = wizardData.receiverType === '04';
    if ((isContribuyente || isGobierno || isExtranjero) && !wizardData.customerName) {
      newErrors.customerName = 'Nombre del receptor es obligatorio (DGI B403)';
    }

    // RUC/DV: requerido para Contribuyente y Gobierno (DGI B402)
    if ((isContribuyente || isGobierno) && !wizardData.customerRuc) {
      newErrors.customerRuc = 'El RUC es requerido para Contribuyente/Gobierno';
    }
    if ((isContribuyente || isGobierno) && !wizardData.customerDv) {
      newErrors.customerDv = 'El DV es requerido para Contribuyente/Gobierno';
    }

    // DGI B402d/B406 no se validan aquí para mantener el mismo flujo del portal legacy

    // DGI B404: Dirección obligatoria solo para Contribuyente (01) y Gobierno (03), opcional para 02/04
    if ((isContribuyente || isGobierno) && !wizardData.customerAddress) {
      newErrors.customerAddress = 'Dirección obligatoria para el receptor (DGI B404)';
    }

    // DGI B405: Ubicación (provincia/distrito/corregimiento) obligatoria solo para
    // Contribuyente (01) y Gobierno (03), NO para Consumidor Final (02) ni Extranjero (04)
    if ((isContribuyente || isGobierno) && !wizardData.customerProvince) {
      newErrors.customerProvince = 'Ubicación obligatoria para Contribuyente/Gobierno (DGI B405)';
    }

    // Validación de email — DGI B409: Ocu 0-3 (siempre opcional para DGI)
    // Para el portal: requerido para Contribuyente/Gobierno/Extranjero (para envío de FE), opcional para Consumidor Final
    if (!wizardData.customerEmail) {
      newErrors.customerEmail = 'El correo electrónico es requerido para enviar la factura';
    } else if (wizardData.customerEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (!emailRegex.test(wizardData.customerEmail)) {
        newErrors.customerEmail = 'El formato del correo electrónico no es válido';
      }
    }

    // --- Validaciones de País ---

    if (!wizardData.customerForeignCountry?.trim()) {
      newErrors.customerCountry = 'País del receptor obligatorio (DGI B410)';
    }

    // NOTA: Las validaciones B410a (país=PA si destino=Panamá) y B410b (país≠PA si destino=Extranjero)
    // NO se validan aquí porque el destino se configura en Step 2.
    // Estas validaciones se ejecutan en el invoiceMapper antes de enviar a Aludra.

    // DGI B08 (1515): Punto de facturación "000" -> R
    if (wizardData.billingPointCode === '000') {
      newErrors.billingPoint = 'El punto de facturación no puede ser "000" (DGI B08)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const displayAddress = () => {
    const parts = [];
    if (wizardData.customerCountry) parts.push(wizardData.customerCountry);
    if (wizardData.customerProvince) parts.push(wizardData.customerProvince);
    if (wizardData.customerDistrict) parts.push(wizardData.customerDistrict);
    if (wizardData.customerTownship) parts.push(wizardData.customerTownship);
    return parts.join(', ') || wizardData.customerAddress || 'Seleccione la dirección';
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">
          Información del Emisor y Receptor
        </h2>
        <p className="mt-2 text-secondary-600 dark:text-secondary-400">
          Complete la información de la compañía emisora y el receptor de la factura
        </p>
      </div>

      {/* Alerta cuando no hay compañía seleccionada */}
      {!selectedCompany && !companyLoading && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                No hay compañía seleccionada
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                <p>
                  Por favor selecciona una compañía desde el selector en la esquina superior derecha de la página.
                  Sin una compañía seleccionada, no se podrá completar la información del emisor.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Información del Emisor */}
        <Card>
          <div className="bg-secondary-50 dark:bg-secondary-800 px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
            <div className="flex items-center gap-2">
              <BuildingOfficeIcon className="h-5 w-5 text-secondary-700 dark:text-secondary-300" />
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                Información de la compañía emisora
              </h3>
            </div>
          </div>
          <CardBody>
            {/* Company Logo */}
            <div className="flex justify-center mb-6">
              {selectedCompany?.profileImage ? (
                <img
                  src={selectedCompany.profileImage}
                  alt={`Logo de ${selectedCompany.name}`}
                  className="w-48 h-32 object-contain rounded-lg bg-white dark:bg-secondary-800 p-2"
                  onError={(e) => {
                    // Fallback if image fails to load
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`w-48 h-32 bg-secondary-100 dark:bg-secondary-700 rounded-lg flex items-center justify-center ${selectedCompany?.profileImage ? 'hidden' : ''}`}>
                <span className="text-secondary-500 dark:text-secondary-400 text-sm">Logo de la compañía</span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Nombre del emisor
                  </label>
                  <Input
                    value={selectedCompany?.fullName || selectedCompany?.name || (companyLoading ? 'Cargando...' : 'No seleccionada')}
                    disabled
                    className="bg-secondary-50 dark:bg-secondary-800"
                  />
                  {!selectedCompany && !companyLoading && (
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                      ⚠️ Por favor selecciona una compañía desde el selector en la esquina superior
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Código de la compañía
                  </label>
                  <Input
                    value={selectedCompany?.code || (companyLoading ? 'Cargando...' : 'N/A')}
                    disabled
                    className="bg-secondary-50 dark:bg-secondary-800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    RUC
                  </label>
                  <Input
                    value={selectedCompany?.numberId || (companyLoading ? 'Cargando...' : 'N/A')}
                    disabled
                    className="bg-secondary-50 dark:bg-secondary-800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    DV
                  </label>
                  <Input
                    value={selectedCompany?.dv || (companyLoading ? 'Cargando...' : 'N/A')}
                    disabled
                    className="bg-secondary-50 dark:bg-secondary-800"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Sucursal a utilizar <span className="text-danger-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    className={`flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white transition-colors ${
                      errors.branchOffice
                        ? 'border-danger-500 dark:border-danger-500 focus:ring-danger-500 focus:border-danger-500'
                        : 'border-secondary-300 dark:border-secondary-600 focus:ring-primary-500 focus:border-primary-500'
                    }`}
                    value={wizardData.branchOfficeId}
                    onChange={(e) => {
                      const selectedBranch = branchOffices.find((b: any) => b.id === e.target.value);
                      onUpdate({
                        branchOfficeId: e.target.value,
                        // Setear coordenadas inmediato desde datos locales (fallback síncrono)
                        // El useEffect async de Aludra las sobrescribirá con datos frescos
                        branchOfficeLatitude: selectedBranch?.coordinates?.latitude?.toString() || '',
                        branchOfficeLongitude: selectedBranch?.coordinates?.longitude?.toString() || '',
                      });
                      // Clear error when user selects a branch
                      if (errors.branchOffice) {
                        setErrors({ ...errors, branchOffice: '' });
                      }
                    }}
                    disabled={loadingBranches || !selectedCompany}
                  >
                    <option value="">
                      {loadingBranches
                        ? 'Cargando sucursales...'
                        : !selectedCompany
                        ? 'Seleccione una compañía primero'
                        : branchOffices && branchOffices.length === 0
                        ? 'No hay sucursales configuradas'
                        : 'Seleccione una sucursal'}
                    </option>
                    {branchOffices?.map((branch: any) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}{branch.fiscalCode ? ` - ${branch.fiscalCode}` : ''}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setIsBranchOfficeModalOpen(true)}
                    disabled={!selectedCompany}
                    title="Crear nueva sucursal"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                {errors.branchOffice && (
                  <p className="text-xs text-danger-600 dark:text-danger-400 mt-1">
                    {errors.branchOffice}
                  </p>
                )}
                {branchOffices && branchOffices.length === 0 && selectedCompany && !errors.branchOffice && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    ⚠️ Esta compañía no tiene sucursales configuradas. Contacte al administrador.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Código del punto de facturación
                </label>
                <Input
                  value={wizardData.billingPointCode}
                  onChange={(e) => {
                    // DGI B08: A 3, solo alfanumérico
                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                    onUpdate({ billingPointCode: val });
                  }}
                  maxLength={3}
                  placeholder="001"
                  className={errors.billingPoint ? 'border-danger-600' : ''}
                />
                {errors.billingPoint && (
                  <p className="text-xs text-danger-600 dark:text-danger-400 mt-1">
                    {errors.billingPoint}
                  </p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Información del Receptor */}
        <Card>
          <div className="bg-secondary-50 dark:bg-secondary-800 px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
            <div className="flex items-center gap-2">
              <UserCircleIcon className="h-5 w-5 text-secondary-700 dark:text-secondary-300" />
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                Información del receptor
              </h3>
            </div>
          </div>
          <CardBody>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Tipo de receptor <span className="text-danger-600">*</span>
                </label>
                <select
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                  value={wizardData.receiverType}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const isNewExtranjero = newType === '04';
                    const isNewConsumidorFinal = newType === '02';
                    onUpdate({
                      receiverType: newType,
                      // No forzar destino al cambiar tipo de receptor
                      // Limpiar campos incompatibles al cambiar tipo de receptor (DGI B406a)
                      ...(isNewExtranjero ? {
                        customerDv: '',
                        customerRecepType: '1',
                        customerForeignId: wizardData.customerRuc || '',
                      } : {}),
                      // Si cambia de Extranjero a otro tipo, limpiar ID extranjero
                      ...(wizardData.receiverType === '04' && !isNewExtranjero ? {
                        customerForeignId: undefined,
                        customerForeignCountry: undefined,
                      } : {}),
                      // Si es Consumidor Final, forzar tipo RUC a Natural (DGI B402d)
                      ...(isNewConsumidorFinal ? { customerRecepType: '1' } : {}),
                      // Resetear tipo de documento a RUC al cambiar tipo de receptor
                      // (evita que quede Pasaporte/Otro seleccionado para Contribuyente/Gobierno)
                      customerRucType: '01',
                    });
                    // Limpiar errores al cambiar tipo
                    setErrors({});
                  }}
                >
                  {RECEIVER_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Nombre del receptor {(wizardData.receiverType === '01' || wizardData.receiverType === '03' || wizardData.receiverType === '04') && <span className="text-danger-600">*</span>}
                  {wizardData.receiverType === '02' && <span className="text-secondary-400 text-xs ml-1">(Opcional)</span>}
                </label>
                <div className="flex gap-2">
                  <Input
                    defaultValue={wizardData.customerName}
                    onBlur={(e) => onUpdate({ customerName: e.target.value })}
                    placeholder="Escriba el nombre del receptor"
                    maxLength={150}
                    className={errors.customerName ? 'border-danger-600' : ''}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setIsCustomerModalOpen(true)}
                    title="Administrador de clientes"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                {errors.customerName && (
                  <p className="text-sm text-danger-600 mt-1">{errors.customerName}</p>
                )}
              </div>

              {/* Tipo de RUC - Ocultar para Extranjero, bloquear para Consumidor Final */}
              {wizardData.receiverType !== '04' && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Tipo de RUC <span className="text-danger-600">*</span>
                  </label>
                  <select
                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white ${
                      errors.customerRecepType
                        ? 'border-danger-500'
                        : 'border-secondary-300 dark:border-secondary-600'
                    } ${wizardData.receiverType === '02' ? 'opacity-60' : ''}`}
                    value={wizardData.customerRecepType}
                    onChange={(e) => onUpdate({ customerRecepType: e.target.value })}
                    disabled={wizardData.receiverType === '02'}
                  >
                    {RUC_PERSON_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {wizardData.receiverType === '02' && (
                    <p className="text-xs text-secondary-500 mt-1">Consumidor Final solo puede ser Natural</p>
                  )}
                  {errors.customerRecepType && (
                    <p className="text-sm text-danger-600 mt-1">{errors.customerRecepType}</p>
                  )}
                </div>
              )}

              {/* RUC/ID y DV - Adaptativo segun tipo de receptor */}
              {wizardData.receiverType === '04' ? (
                /* Extranjero: mostrar campo de ID extranjero sin DV */
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    ID Extranjero (Pasaporte o ID Tributario) <span className="text-danger-600">*</span>
                  </label>
                  <Input
                    defaultValue={wizardData.customerRuc}
                    onBlur={(e) => onUpdate({ customerRuc: e.target.value })}
                    placeholder="Ej: 081-101182-0004L"
                    maxLength={50}
                    className={errors.customerRuc ? 'border-danger-600' : ''}
                  />
                  {errors.customerRuc && (
                    <p className="text-sm text-danger-600 mt-1">{errors.customerRuc}</p>
                  )}
                </div>
              ) : (
                /* Contribuyente/Gobierno/Consumidor Final: RUC + DV */
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      {wizardData.receiverType === '02' ? 'RUC o Cédula' : 'RUC'} {(wizardData.receiverType === '01' || wizardData.receiverType === '03') && <span className="text-danger-600">*</span>}
                      {wizardData.receiverType === '02' && <span className="text-secondary-400 text-xs ml-1">(Opcional)</span>}
                    </label>
                    <Input
                      defaultValue={wizardData.customerRuc}
                      onBlur={(e) => onUpdate({ customerRuc: e.target.value })}
                      placeholder={wizardData.receiverType === '02' ? 'Cédula del ciudadano (opcional)' : 'RUC Ejem: 101991-11-00001'}
                      maxLength={20}
                      className={errors.customerRuc ? 'border-danger-600' : ''}
                    />
                    {errors.customerRuc && (
                      <p className="text-sm text-danger-600 mt-1">{errors.customerRuc}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      DV {(wizardData.receiverType === '01' || wizardData.receiverType === '03') && <span className="text-danger-600">*</span>}
                      {wizardData.receiverType === '02' && <span className="text-secondary-400 text-xs ml-1">(Opcional)</span>}
                    </label>
                    <Input
                      defaultValue={wizardData.customerDv}
                      onBlur={(e) => onUpdate({ customerDv: e.target.value })}
                      placeholder="02"
                      maxLength={2}
                      type="number"
                      className={errors.customerDv ? 'border-danger-600' : ''}
                    />
                    {errors.customerDv && (
                      <p className="text-sm text-danger-600 mt-1">{errors.customerDv}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Tipo de Documento — según tipo de receptor DGI */}
              {wizardData.receiverType !== '04' && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Tipo de Documento <span className="text-danger-600">*</span>
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white"
                    value={wizardData.customerRucType}
                    onChange={(e) => onUpdate({ customerRucType: e.target.value })}
                  >
                    {(DOCUMENT_TYPES_BY_RECEIVER[wizardData.receiverType] || DOCUMENT_TYPES_BY_RECEIVER['01']).map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {wizardData.receiverType === '02' && wizardData.customerRucType === '02' && (
                    <p className="text-xs text-secondary-500 mt-1">DGI permite enviar cédula en lugar de RUC para Consumidor Final (B4022)</p>
                  )}
                </div>
              )}

           

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Correo electrónico del receptor <span className="text-danger-600">*</span>
                  </label>
                  <Input
                    type="email"
                    defaultValue={wizardData.customerEmail}
                    onBlur={(e) => onUpdate({ customerEmail: e.target.value })}
                    placeholder="correo@ejemplo.com"
                    maxLength={50}
                    className={errors.customerEmail ? 'border-danger-600' : ''}
                  />
                  {errors.customerEmail && (
                    <p className="text-sm text-danger-600 mt-1">{errors.customerEmail}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                    Teléfono del receptor
                  </label>
                  <Input
                    type="tel"
                    defaultValue={wizardData.customerPhone || ''}
                    onBlur={(e) => {
                      const value = e.target.value.replace(/[^\d-+]/g, '');
                      onUpdate({ customerPhone: value.trim() });
                    }}
                    placeholder="6000-0000 o +507-6000-0000"
                    maxLength={16}
                    helperText="Formato: 0000-0000, 000-0000 o +507-0000-0000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Dirección <span className="text-danger-600">*</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    value={displayAddress()}
                    readOnly
                    placeholder="Seleccione la dirección del receptor"
                    className={`flex-1 cursor-pointer ${errors.customerAddress ? 'border-danger-600' : ''}`}
                    onClick={() => setIsAddressModalOpen(true)}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setIsAddressModalOpen(true)}
                    title="Seleccionar dirección"
                  >
                    <MapPinIcon className="w-4 h-4" />
                  </Button>
                </div>
                {errors.customerAddress && (
                  <p className="text-sm text-danger-600 mt-1">{errors.customerAddress}</p>
                )}
                {errors.customerProvince && (
                  <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">{errors.customerProvince}</p>
                )}
                {errors.customerCountry && (
                  <p className="text-sm text-danger-600 mt-1">{errors.customerCountry}</p>
                )}
              </div>

              {/* Advertencia para Gobierno: necesitará retención y CPBS */}
              {wizardData.receiverType === '03' && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300 font-medium">Requisitos para ventas al Gobierno:</p>
                  <ul className="text-xs text-blue-600 dark:text-blue-400 mt-1 list-disc list-inside space-y-0.5">
                    <li>Los ítems deben incluir el código CPBS completo (Codificación Panameña de Bienes y Servicios)</li>
                    <li>Se debe configurar la retención aplicable en el paso de notas adicionales</li>
                  </ul>
                </div>
              )}

            </div>
          </CardBody>
        </Card>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleNext}
          disabled={!selectedCompany || companyLoading}
        >
          Siguiente
        </Button>
      </div>

      {/* Mensaje de ayuda cuando el botón está deshabilitado */}
      {!selectedCompany && !companyLoading && (
        <div className="text-sm text-secondary-500 dark:text-secondary-400 text-right">
          Selecciona una compañía para continuar
        </div>
      )}

      {/* Modals */}
      {isCustomerModalOpen && (
        <CustomerSearchModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSelectCustomer={handleSelectCustomer}
        />
      )}

      {isAddressModalOpen && (
        <AddressModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          onSave={handleAddressUpdate}
          customerAddresses={selectedCustomer?.address}
          receiverType={wizardData.receiverType}
          initialData={{
            description: wizardData.customerAddress,
            country: wizardData.customerCountry,
            countryCode: wizardData.customerForeignCountry,
            province: wizardData.customerProvince,
            district: wizardData.customerDistrict,
            township: wizardData.customerTownship,
            city: wizardData.customerCity,
            fiscalCode: wizardData.customerAddressFiscalCode,
          }}
        />
      )}

      {isBranchOfficeModalOpen && (
        <BranchOfficeFormModal
          isOpen={isBranchOfficeModalOpen}
          onClose={() => setIsBranchOfficeModalOpen(false)}
          onBranchOfficeCreated={(branchOffice) => {
            // Auto-select the newly created branch office
            onUpdate({ branchOfficeId: branchOffice.BranchOfficeId });
            setIsBranchOfficeModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
